import { supabase } from "@/integrations/supabase/client";

/**
 * Wrapper for the `google-calendar-tools` edge function with:
 *  - automatic retry for transient TOKEN_REFRESH_IN_PROGRESS (409) errors
 *  - typed error classification (needs_reconnect, slot_busy, ambiguous_match, transient, fatal)
 *
 * Does NOT change the edge function action signatures — safe to use anywhere
 * (including alongside the AI agent which calls the function directly).
 */

const RETRY_DELAYS_MS = [1500, 2500];

export type CalendarFailureKind =
  | "needs_reconnect"
  | "slot_busy"
  | "ambiguous_match"
  | "transient"
  | "fatal";

export type CalendarInvokeResult<T> =
  | { ok: true; kind: "ok"; data: T; message?: string }
  | {
      ok: false;
      kind: CalendarFailureKind;
      message: string;
      conflictingEvent?: any;
      candidates?: any[];
      raw?: any;
    };

function classify(
  error: any,
  data: any,
): { kind: CalendarFailureKind; msg: string } | null {
  const blob = JSON.stringify({
    e: error?.message ?? error ?? null,
    d: data ?? null,
  }).toLowerCase();

  if (blob.includes("google_auth_needs_reconnect")) {
    return { kind: "needs_reconnect", msg: "Reconecte o Google Calendar para continuar." };
  }
  if (blob.includes("token_refresh_in_progress")) {
    return { kind: "transient", msg: "Sincronização com Google Calendar em andamento." };
  }
  if (data?.error === "event_overlap") {
    return { kind: "slot_busy", msg: data.message ?? "Horário ocupado no Google Calendar." };
  }
  if (data?.error === "ambiguous_match") {
    return {
      kind: "ambiguous_match",
      msg: data.message ?? "Múltiplos eventos similares encontrados no Google Calendar.",
    };
  }
  if (error || data?.error) {
    return {
      kind: "fatal",
      msg: data?.message ?? error?.message ?? "Erro ao sincronizar com Google Calendar.",
    };
  }
  return null;
}

export async function invokeGoogleCalendar<T = any>(
  action: string,
  workspaceId: string,
  params: Record<string, unknown> = {},
): Promise<CalendarInvokeResult<T>> {
  let lastTransientMsg = "Sincronização com Google Calendar em andamento.";

  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    const { data, error } = await supabase.functions.invoke("google-calendar-tools", {
      body: { action, workspaceId, params },
    });

    const c = classify(error, data);
    if (!c) {
      return { ok: true, kind: "ok", data: data as T };
    }

    if (c.kind === "transient" && attempt < RETRY_DELAYS_MS.length) {
      lastTransientMsg = c.msg;
      console.log(
        `[invokeGoogleCalendar] transient on action=${action} (attempt ${attempt + 1}), retrying in ${RETRY_DELAYS_MS[attempt]}ms`,
      );
      await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt]));
      continue;
    }

    if (c.kind === "slot_busy") {
      return {
        ok: false,
        kind: "slot_busy",
        message: c.msg,
        conflictingEvent: data?.conflictingEvent,
      };
    }
    if (c.kind === "ambiguous_match") {
      return {
        ok: false,
        kind: "ambiguous_match",
        message: c.msg,
        candidates: data?.candidates,
      };
    }
    if (c.kind === "needs_reconnect") {
      return { ok: false, kind: "needs_reconnect", message: c.msg };
    }
    if (c.kind === "transient") {
      return { ok: false, kind: "transient", message: c.msg };
    }
    return { ok: false, kind: "fatal", message: c.msg, raw: data ?? error };
  }

  return { ok: false, kind: "transient", message: lastTransientMsg };
}
